// src/pages/ParentReport.js
//
// 👪 FOR GROWN-UPS — route /grown-ups. A parent (or a curious student) sees,
// for the signed-in child's account:
//   • their avatar, coins, coins earned in all, gifts waiting, stickers found
//   • per subject: chests opened (games finished), videos, songs and
//     sing-alongs, with a progress bar and the topics done / still to do
//   • the latest coin activity (what each coin was for)
// Everything is read live from the account's own data — nothing extra is
// stored. Parent accounts share their child's login, so this is the child's
// learning space seen through a grown-up's eyes.

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { collection, doc, getDoc, limit, onSnapshot, orderBy, query } from "firebase/firestore";
import { db } from "../firebase/firebaseConfig";
import { gradeToken } from "../config/plans";
import { NAV_SUBJECTS } from "../data/subjects";
import { getPortalLayout, makeLayoutId } from "../services/layoutService";
import { useWallet } from "../services/coinService";
import { useChests, useCollection, REWARDS } from "../services/rewardsService";
import MyAvatar from "../components/avatar/MyAvatar";
import "./parentReport.css";

const done = (v) => (typeof v === "boolean" ? v : Boolean(v && (v.completed || v.done)));
const when = (at) => {
  const d = at && typeof at.toDate === "function" ? at.toDate() : null;
  return d ? d.toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "";
};

const ParentReport = () => {
  const navigate = useNavigate();
  const wallet = useWallet();
  const { chests } = useChests();
  const col = useCollection();
  const [profile, setProfile] = useState(null);
  const [data, setData] = useState({});
  const [layouts, setLayouts] = useState({});
  const [ledger, setLedger] = useState([]);
  const [open, setOpen] = useState("");

  useEffect(() => {
    let stops = [];
    const stopAuth = onAuthStateChanged(getAuth(), async (user) => {
      stops.forEach((f) => f());
      stops = [];
      if (!user) {
        navigate("/login");
        return;
      }
      const p = await getDoc(doc(db, "users", user.uid));
      setProfile(p.exists() ? p.data() : {});
      stops.push(onSnapshot(doc(db, "userData", user.uid), (s) => setData((s.exists() && s.data()) || {}), () => {}));
      stops.push(
        onSnapshot(
          query(collection(db, "wallets", user.uid, "ledger"), orderBy("at", "desc"), limit(15)),
          (s) => setLedger(s.docs.map((d) => ({ id: d.id, ...d.data() }))),
          () => {}
        )
      );
    });
    return () => {
      stops.forEach((f) => f());
      stopAuth();
    };
  }, [navigate]);

  const state = String(profile?.state || String(profile?.role || "").split("-")[0] || "").toLowerCase();
  const grade = profile?.grade ? gradeToken(profile.grade) : String(profile?.role || "").split("-")[1] || "";

  // each subject's topics (student view of the class)
  useEffect(() => {
    if (!state || !grade) return;
    NAV_SUBJECTS.forEach((s) => {
      getPortalLayout(makeLayoutId({ state, grade, subject: s.value, view: "student" }))
        .then((l) => setLayouts((prev) => ({ ...prev, [s.value]: l })))
        .catch(() => setLayouts((prev) => ({ ...prev, [s.value]: null })));
    });
  }, [state, grade]);

  const subjects = useMemo(
    () =>
      NAV_SUBJECTS.map((s) => {
        const topics = [...(layouts[s.value]?.topics || [])].filter((t) => t && t.slug && t.visible !== false).sort((a, b) => (a.order || 0) - (b.order || 0));
        const mine = data[`${state}_${grade}_${s.value}`] || {};
        const rows = topics.map((t) => {
          const e = mine[t.slug] || {};
          return { ...t, game: done(e.game), video: done(e.video), music: done(e.music || e.song), sing: done(e.singalong || e.singAlong) };
        });
        const count = (k) => rows.filter((r) => r[k]).length;
        return { ...s, rows, chests: count("game"), videos: count("video"), songs: count("music"), sings: count("sing"), loaded: s.value in layouts };
      }),
    [layouts, data, state, grade]
  );

  const stickersFound = Object.values(col.stickers || {}).filter((n) => Number(n) > 0).length;
  const stickersAll = (REWARDS.albums || []).reduce((n, a) => n + (a.stickers?.length || 0), 0);
  const name = profile?.firstName || "Your child";

  return (
    <div className="pr-page">
      <button type="button" className="pr-back" onClick={() => navigate("/dashboard")}>
        ← Back to the dashboard
      </button>

      <header className="pr-hero">
        <div className="pr-hero-av">
          <MyAvatar size={110} fullSize={200} toggle />
        </div>
        <div>
          <p className="pr-kicker">👪 For grown-ups</p>
          <h1>{name}'s learning report</h1>
          <p className="pr-sub">
            {[String(state).toUpperCase(), grade].filter(Boolean).join(" · ")} — everything updates live as {name} plays.
          </p>
        </div>
      </header>

      <section className="pr-stats">
        <div>
          <b>{wallet.coins.toLocaleString()}</b>
          <span>coins now</span>
        </div>
        <div>
          <b>{(wallet.lifetimeCoins || 0).toLocaleString()}</b>
          <span>coins earned in all</span>
        </div>
        <div>
          <b>{subjects.reduce((n, s) => n + s.chests, 0)}</b>
          <span>treasure chests opened</span>
        </div>
        <div>
          <b>
            {stickersFound}
            {stickersAll ? ` / ${stickersAll}` : ""}
          </b>
          <span>stickers found</span>
        </div>
        <div>
          <b>{chests.length}</b>
          <span>gifts waiting to open</span>
        </div>
      </section>

      <h2 className="pr-h2">Subjects</h2>
      <div className="pr-subjects">
        {subjects.map((s) => {
          const total = s.rows.length;
          const pct = total ? Math.round((s.chests / total) * 100) : 0;
          const isOpen = open === s.value;
          return (
            <article key={s.value} className={`pr-subject ${isOpen ? "is-open" : ""}`}>
              <button type="button" className="pr-subject-head" onClick={() => setOpen(isOpen ? "" : s.value)} aria-expanded={isOpen}>
                <span className="pr-subject-icon">{s.icon}</span>
                <span className="pr-subject-name">
                  <b>{s.label}</b>
                  <small>{!s.loaded ? "Loading…" : total ? `${s.chests} of ${total} topics finished` : "No topics yet"}</small>
                </span>
                <span className="pr-subject-bar">
                  <span style={{ width: `${pct}%` }} />
                </span>
                <span className="pr-subject-pct">{pct}%</span>
              </button>
              {isOpen && total > 0 && (
                <div className="pr-topics">
                  <div className="pr-topic-legend">
                    <span>🎬 video</span>
                    <span>🎵 song</span>
                    <span>🎤 sing-along</span>
                    <span>🎮 game (opens the chest)</span>
                  </div>
                  {s.rows.map((t) => (
                    <div key={t.slug} className={`pr-topic ${t.game ? "is-done" : ""}`}>
                      <span className="pr-topic-title">{t.title || t.slug}</span>
                      <span className={t.video ? "on" : ""} title="Video">
                        🎬
                      </span>
                      <span className={t.music ? "on" : ""} title="Song">
                        🎵
                      </span>
                      <span className={t.sing ? "on" : ""} title="Sing-along">
                        🎤
                      </span>
                      <span className={t.game ? "on" : ""} title="Game">
                        🎮
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </article>
          );
        })}
      </div>

      <h2 className="pr-h2">Recent coins</h2>
      <div className="pr-ledger">
        {ledger.length === 0 && <p className="pr-empty">No coin activity yet.</p>}
        {ledger.map((e) => (
          <div key={e.id} className="pr-ledger-row">
            <span className="pr-ledger-date">{when(e.at)}</span>
            <span className="pr-ledger-what">{e.reason === "cheer" ? `📣 Teacher cheer: ${e.label || ""}` : e.label || e.reason}</span>
            {Number(e.amount) ? <span className={`pr-ledger-amt ${Number(e.amount) < 0 ? "is-neg" : ""}`}>{Number(e.amount) > 0 ? `+${e.amount}` : e.amount}</span> : <span />}
          </div>
        ))}
      </div>
    </div>
  );
};

export default ParentReport;
